import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, Type } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IUserTag } from '@lb/api/mylb/interfaces';
import { AdminPeopleService } from '@lb/api/mylb/services/admin-people.service';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import {
	Day,
	IMDDropDownOption,
	MD_DIALOG_REF,
	MDDatePickerInputDirective,
	MDDialogModule,
	MDDialogService,
	MDDropDownComponent
} from '@melodic-ui/components';
import { isPast, format, parse } from 'date-fns';
import { take } from 'rxjs/internal/operators/take';
import { AdminStateService } from '../../../apps/admin/src/app/shared/state/admin-state.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { filter } from 'rxjs';

@Component({
	selector: 'lb-user-tag-dialog',
	standalone: true,
	templateUrl: './user-tag-dialog.component.html',
	styleUrls: ['./user-tag-dialog.component.scss'],
	imports: [MDDialogModule, CommonModule, LoaderComponent, ReactiveFormsModule, MDDropDownComponent, MDDatePickerInputDirective]
})
export class UserTagDialogComponent implements OnInit {
	private readonly dialogRef = inject(MD_DIALOG_REF);
	private readonly adminPeopleService = inject(AdminPeopleService);
	private readonly adminStateService = inject(AdminStateService);
	private readonly fb = inject(FormBuilder);
	private readonly dialogService = inject(MDDialogService);

	userID = input.required<number>();
	userType = input.required<'LEARNER' | 'INSTRUCTOR' | 'CONTACT'>();
	userTag = input<IUserTag | null>(null);

	userTagTypesLoading = this.adminStateService.loading;

	tagForm!: FormGroup;

	tagTypes = computed(() => this.adminStateService.userTagTypes().filter((tagType) => tagType.tagUserType === this.userType()));
	tagOptions = computed<IMDDropDownOption[]>(() => {
		// System tag types (e.g. Under 13) are applied/removed by scheduled jobs, never by users.
		return this.tagTypes()
			.filter((tagType) => !tagType.isSystem)
			.map((tagType) => ({
				label: tagType.tagTypeName,
				value: tagType.userTagTypeID
			}));
	});
	isEdit = computed(() => this.userTag() !== null);

	ngOnInit(): void {
		this.tagForm = this.fb.group({
			tagType: [-1, [Validators.required]],
			notes: [''],
			returnDate: [null]
		});

		const userTag = this.userTag();

		if (userTag) {
			// Edit existing tag — ensure date-only handling in local timezone
			const returnDateStr = userTag.returnDate ? this.toDateInputString(userTag.returnDate as unknown as Date | string) : null;
			this.tagForm.patchValue({
				tagType: userTag.userTagTypeID,
				notes: userTag.notes || '',
				returnDate: returnDateStr
			});
		}
	}

	// Formats a Date or ISO/date string to 'YYYY-MM-DD' using local time, avoiding UTC shifts
	private toDateInputString(dateOrString: Date | string): string {
		if (typeof dateOrString === 'string') {
			// If already a plain date string (YYYY-MM-DD), return as-is
			if (/^\d{4}-\d{2}-\d{2}$/.test(dateOrString)) {
				return dateOrString;
			}
			// Otherwise, parse as Date and format locally
			const d = new Date(dateOrString);
			return format(d, 'yyyy-MM-dd');
		}
		return format(dateOrString, 'yyyy-MM-dd');
	}

	// Parses a 'YYYY-MM-DD' string (or Date) into a Date using local time to prevent timezone-day drift
	private parseDateOnlyInput(value: string | Date | null): Date | null {
		if (!value) return null;
		if (value instanceof Date) return value;
		if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
			// Parse as local date (year, monthIndex, day)
			const parsed = parse(value, 'yyyy-MM-dd', new Date());
			return parsed;
		}
		// Fallback: generic Date parsing
		return new Date(value);
	}

	setTagType(event: unknown): void {
		const tagType = event as number;
		this.tagForm.get('tagType')?.setValue(tagType);
	}

	disablePastDates(): (day: Day) => boolean {
		return (day: Day) => isPast(day.date);
	}

	close(): void {
		this.dialogRef.close();
	}

	confirmDelete(): void {
		const userTag = this.userTag();

		// System tags never open this dialog; the API rejects them regardless.
		if (userTag?.isSystem) {
			return;
		}

		if (userTag) {
			this.dialogService
				.open(ConfirmationDialogComponent as Type<Component>, {
					title: 'Confirm Delete',
					body: 'Are you sure you want to delete this user tag? This action cannot be undone.',
					icon: 'untitled-ui/trash/trash-01',
					successText: 'Delete'
				})
				.afterClosed.pipe(
					take(1),
					filter((x) => !!x),
					switchMap(() => this.adminPeopleService.deleteUserTag(userTag.userTagID).pipe(take(1)))
				)
				.subscribe(() => this.dialogRef.close(true));
		}
	}

	confirm(): void {
		// System tags never open this dialog; the API rejects them regardless.
		if (this.userTag()?.isSystem) {
			return;
		}

		if (this.tagForm.valid) {
			const rdValue = this.tagForm.value.returnDate as string | Date | null;
			const existingTag = this.userTag();
			const tagTypeControlValue = this.tagForm.get('tagType')?.value;
			const tagTypeValue = typeof tagTypeControlValue === 'number' ? tagTypeControlValue : Number(tagTypeControlValue);
			const userTag: IUserTag = {
				userTagID: this.isEdit() && existingTag ? existingTag.userTagID : -1,
				userID: this.userID(),
				userTagTypeID: tagTypeValue,
				tagTypeName: '',
				tagTypeAbbreviation: '',
				notes: this.tagForm.value.notes || '',
				returnDate: this.parseDateOnlyInput(rdValue),
				// Users can only ever create ordinary tags; the dropdown filters system types out.
				isSystem: false,
				isActive: true,
				createDate: new Date(),
				lastModifiedDate: null
			};

			this.adminPeopleService
				.upsertUserTag(userTag)
				.pipe(take(1))
				.subscribe(() => this.dialogRef.close(true));
		} else {
			this.tagForm.markAllAsTouched();
		}
	}
}
